import { supabase } from './supabase';
import { getSiteUrl } from './siteConfig';
import { getDamageReportByBookingId, generateDamageReportSummary, DAMAGE_TYPES, SEVERITY_LEVELS } from './damageReports';
import { sendCustomMessage } from './messaging';
import { sendGmailEmail } from './email';
import html2pdf from 'html2pdf.js';

export interface LiabilityWaiver {
  id: string;
  business_id: string;
  booking_id: string;
  token: string;
  customer_name: string;
  customer_email?: string;
  customer_phone?: string;
  terms_content: string;
  agreed_at?: string;
  signature_data?: string;
  pdf_url?: string;
  ip_address?: string;
  user_agent?: string;
  created_at: string;
  updated_at: string;
}

/**
 * Generate a liability waiver for a booking
 */
export async function generateLiabilityWaiver(bookingId: string): Promise<string | null> {
  if (!supabase) {
    throw new Error('Supabase client not initialized');
  }

  try {
    const { data, error } = await supabase.rpc('create_liability_waiver_for_booking', {
      p_booking_id: bookingId
    });

    if (error) {
      console.error('Error generating liability waiver:', error);
      throw error;
    }

    return data; // Returns the token
  } catch (err) {
    console.error('Exception generating liability waiver:', err);
    throw err;
  }
}

/**
 * Get liability waiver by token (public access)
 */
export async function getLiabilityWaiverByToken(token: string): Promise<LiabilityWaiver | null> {
  if (!supabase) {
    throw new Error('Supabase client not initialized');
  }

  try {
    const { data, error } = await supabase
      .from('liability_waivers')
      .select('*')
      .eq('token', token)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null; // No waiver found
      }
      throw error;
    }

    return data;
  } catch (err) {
    console.error('Error fetching liability waiver by token:', err);
    throw err;
  }
}

/**
 * Get liability waiver by booking ID
 */
export async function getLiabilityWaiverByBookingId(bookingId: string): Promise<LiabilityWaiver | null> {
  if (!supabase) {
    throw new Error('Supabase client not initialized');
  }

  try {
    const { data, error } = await supabase
      .from('liability_waivers')
      .select('*')
      .eq('booking_id', bookingId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null; // No waiver found
      }
      throw error;
    }

    return data;
  } catch (err) {
    console.error('Error fetching liability waiver by booking ID:', err);
    throw err;
  }
}

/**
 * Submit signed liability waiver
 */
export async function submitLiabilityWaiver(
  token: string,
  signatureData: string,
  ipAddress?: string,
  userAgent?: string
): Promise<boolean> {
  if (!supabase) {
    throw new Error('Supabase client not initialized');
  }

  try {
    // Update the waiver with signature data
    const { error } = await supabase
      .from('liability_waivers')
      .update({
        agreed_at: new Date().toISOString(),
        signature_data: signatureData,
        ip_address: ipAddress,
        user_agent: userAgent,
        updated_at: new Date().toISOString()
      })
      .eq('token', token);

    if (error) {
      console.error('Error submitting liability waiver:', error);
      throw error;
    }

    // After successful submission, get the updated waiver and generate PDF
    console.log('✅ [LIABILITY WAIVER] Waiver submitted successfully, generating PDF...');
    
    const updatedWaiver = await getLiabilityWaiverByToken(token);
    if (updatedWaiver && updatedWaiver.agreed_at) {
      // Generate PDF in the background (don't block the response)
      generateAndStoreLiabilityWaiverPDF(updatedWaiver).catch(error => {
        console.error('❌ [LIABILITY WAIVER] Failed to generate PDF after submission:', error);
        // Don't throw here as the waiver was successfully submitted
      });
    }

    return true;
  } catch (err) {
    console.error('Exception submitting liability waiver:', err);
    throw err;
  }
}

/**
 * Generate liability waiver URL
 */
export function generateLiabilityWaiverURL(token: string): string {
  const baseURL = getSiteUrl();
  return `${baseURL}/liability-waiver/${token}`;
}

/**
 * Check if liability waiver is enabled for business
 */
export async function isLiabilityWaiverEnabled(businessId: string): Promise<boolean> {
  if (!supabase) {
    throw new Error('Supabase client not initialized');
  }

  try {
    const { data, error } = await supabase
      .from('businesses')
      .select('liability_waiver_enabled')
      .eq('id', businessId)
      .single();

    if (error) {
      console.error('Error checking liability waiver enabled status:', error);
      return false;
    }

    return data?.liability_waiver_enabled === true;
  } catch (err) {
    console.error('Exception checking liability waiver enabled status:', err);
    return false;
  }
}

/**
 * Send liability waiver via SMS
 */
export async function sendLiabilityWaiverSMS(
  bookingId: string,
  phoneNumber: string,
  customerName: string,
  businessName: string
): Promise<boolean> {
  if (!supabase) {
    throw new Error('Supabase client not initialized');
  }

  try {
    // Get business ID from booking
    const { data: booking, error: bookingError } = await supabase
      .from('bookings')
      .select('business_id')
      .eq('id', bookingId)
      .single();

    if (bookingError || !booking) {
      throw new Error('Failed to get booking information');
    }

    // Check if there's a damage report for this booking
    const damageReport = await getDamageReportByBookingId(bookingId);
    
    // Generate or get existing waiver token
    const token = await generateLiabilityWaiver(bookingId);
    if (!token) {
      throw new Error('Failed to generate liability waiver token');
    }

    // If damage report exists, update the waiver content to include it
    if (damageReport) {
      try {
        const damageReportSummary = generateDamageReportSummary(damageReport);
        
        // Get the default waiver content
        const defaultContent = await getLiabilityWaiverContent(booking.business_id);
        
        // Combine damage report with liability waiver
        const combinedContent = `${damageReportSummary}\n\n--- LIABILITY WAIVER AGREEMENT ---\n\n${defaultContent}`;
        
        // Update the waiver with combined content
        const { error: updateError } = await supabase
          .from('liability_waivers')
          .update({
            terms_content: combinedContent,
            updated_at: new Date().toISOString()
          })
          .eq('token', token);
          
        if (updateError) {
          console.warn('Failed to update waiver with damage report:', updateError);
          // Continue with regular waiver if update fails
        }
      } catch (error) {
        console.warn('Error including damage report in waiver:', error);
        // Continue with regular waiver if damage report integration fails
      }
    }

    // Generate waiver URL
    const waiverURL = generateLiabilityWaiverURL(token);

    // Create SMS message
    const message = `Hi ${customerName}, please sign your liability waiver for ${businessName}: ${waiverURL}`;

    // Send SMS using the existing messaging function
    const result = await sendCustomMessage(
      booking.business_id,
      phoneNumber,
      message
    );

    if (!result.success) {
      throw new Error(result.error || 'Failed to send SMS');
    }

    return true;
  } catch (err) {
    console.error('Exception sending liability waiver SMS:', err);
    throw err;
  }
}

/**
 * Send liability waiver via email
 */
export async function sendLiabilityWaiverEmail(
  bookingId: string,
  customerEmail: string,
  customerName: string,
  businessName: string
): Promise<boolean> {
  if (!supabase) {
    throw new Error('Supabase client not initialized');
  }

  try {
    // Get business ID from booking
    const { data: booking, error: bookingError } = await supabase
      .from('bookings')
      .select('business_id')
      .eq('id', bookingId)
      .single();

    if (bookingError || !booking) {
      throw new Error('Failed to get booking information');
    }

    // Check if there's a damage report for this booking
    const damageReport = await getDamageReportByBookingId(bookingId);
    
    // Generate or get existing waiver token
    const token = await generateLiabilityWaiver(bookingId);
    if (!token) {
      throw new Error('Failed to generate liability waiver token');
    }

    // If damage report exists, update the waiver content to include it
    if (damageReport) {
      try {
        const damageReportSummary = generateDamageReportSummary(damageReport);
        
        // Get the default waiver content
        const defaultContent = await getLiabilityWaiverContent(booking.business_id);
        
        // Combine damage report with liability waiver
        const combinedContent = `${damageReportSummary}\n\n--- LIABILITY WAIVER AGREEMENT ---\n\n${defaultContent}`;
        
        // Update the waiver with combined content
        const { error: updateError } = await supabase
          .from('liability_waivers')
          .update({
            terms_content: combinedContent,
            updated_at: new Date().toISOString()
          })
          .eq('token', token);
          
        if (updateError) {
          console.warn('Failed to update waiver with damage report:', updateError);
          // Continue with regular waiver if update fails
        }
      } catch (error) {
        console.warn('Error including damage report in waiver:', error);
        // Continue with regular waiver if damage report integration fails
      }
    }

    // Generate waiver URL
    const waiverURL = generateLiabilityWaiverURL(token);

    // Send email using Gmail integration
    const emailResult = await sendGmailEmail({
      businessId: booking.business_id,
      to: customerEmail,
      subject: `Liability Waiver Required - ${businessName}`,
      body: `Hi ${customerName},\n\nPlease complete your liability waiver for ${businessName} by clicking the link below:\n\n${waiverURL}\n\nThis waiver is required before your service appointment.\n\nThank you,\n${businessName}`
    });

    if (!emailResult.success) {
      throw new Error(emailResult.error || 'Failed to send email');
    }

    return true;
  } catch (err) {
    console.error('Exception sending liability waiver email:', err);
    throw err;
  }
}

/**
 * Get liability waiver content for business
 */
export async function getLiabilityWaiverContent(businessId: string): Promise<string | null> {
  if (!supabase) {
    throw new Error('Supabase client not initialized');
  }

  try {
    const { data, error } = await supabase
      .from('businesses')
      .select('liability_waiver_content')
      .eq('id', businessId)
      .single();

    if (error) {
      console.error('Error fetching liability waiver content:', error);
      return null;
    }

    return data?.liability_waiver_content || null;
  } catch (err) {
    console.error('Exception fetching liability waiver content:', err);
    return null;
  }
}

/**
 * Update liability waiver content for business
 */
export async function updateLiabilityWaiverContent(
  businessId: string,
  content: string
): Promise<boolean> {
  if (!supabase) {
    throw new Error('Supabase client not initialized');
  }

  try {
    const { error } = await supabase
      .from('businesses')
      .update({ liability_waiver_content: content })
      .eq('id', businessId);

    if (error) {
      console.error('Error updating liability waiver content:', error);
      throw error;
    }

    return true;
  } catch (err) {
    console.error('Exception updating liability waiver content:', err);
    throw err;
  }
}

/**
 * Generate PDF for signed liability waiver
 */
async function generateLiabilityWaiverPDF(waiver: LiabilityWaiver): Promise<Blob> {
  return new Promise(async (resolve, reject) => {
    try {
      console.log('🔄 [LIABILITY PDF] Starting PDF generation...');
      
      // Load damage report if exists
      let damageReport = null;
      try {
        damageReport = await getDamageReportByBookingId(waiver.booking_id);
        if (damageReport) {
          console.log('✅ [LIABILITY PDF] Found damage report with', damageReport.damage_points.length, 'damage points');
        }
      } catch (err) {
        console.warn('⚠️ [LIABILITY PDF] Could not load damage report:', err);
      }
      
      // Create temporary element for PDF generation
      const tempElement = document.createElement('div');
      tempElement.innerHTML = '<p>temp</p>';
      document.body.appendChild(tempElement);

      const pdfInstance = html2pdf().from(tempElement).set({});
      
      pdfInstance.toPdf().get('pdf').then(async (pdf: any) => {
        console.log('✅ [LIABILITY PDF] Successfully accessed jsPDF!');
        
        // Clean up temp element
        document.body.removeChild(tempElement);
        
        // Clear the PDF and start fresh
        pdf.deletePage(1);
        pdf.addPage();
        
        // Set up page dimensions
        const pageWidth = pdf.internal.pageSize.getWidth();
        const leftMargin = 20;
        let yPosition = 30;
        
        // Header
        pdf.setFontSize(20);
        pdf.setFont(undefined, 'bold');
        pdf.setTextColor(0, 0, 0);
        pdf.text('SIGNED LIABILITY WAIVER', leftMargin, yPosition);
        yPosition += 20;
        
        // Waiver details
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'normal');
        pdf.setTextColor(60, 60, 60);
        
        // Customer Information Section
        pdf.setFont(undefined, 'bold');
        pdf.text('Customer Information:', leftMargin, yPosition);
        yPosition += 8;
        
        pdf.setFont(undefined, 'normal');
        pdf.text(`Name: ${waiver.customer_name}`, leftMargin, yPosition);
        yPosition += 6;
        
        if (waiver.customer_email) {
          pdf.text(`Email: ${waiver.customer_email}`, leftMargin, yPosition);
          yPosition += 6;
        }
        
        if (waiver.customer_phone) {
          pdf.text(`Phone: ${waiver.customer_phone}`, leftMargin, yPosition);
          yPosition += 6;
        }
        
        yPosition += 10;
        
        // Agreement Date Section
        pdf.setFont(undefined, 'bold');
        pdf.text('Agreement Date:', leftMargin, yPosition);
        yPosition += 8;
        
        pdf.setFont(undefined, 'normal');
        const signedDate = waiver.agreed_at ? new Date(waiver.agreed_at).toLocaleDateString() : 'Not signed';
        pdf.text(`Signed on: ${signedDate}`, leftMargin, yPosition);
        yPosition += 15;

        // Vehicle Damage Report Section (if exists)
        if (damageReport && damageReport.damage_points.length > 0) {
          console.log('📋 [LIABILITY PDF] Adding vehicle damage report to PDF...');
          
          // Check if we need a new page
          if (yPosition > pdf.internal.pageSize.getHeight() - 150) {
            pdf.addPage();
            yPosition = 30;
          }

          pdf.setFont(undefined, 'bold');
          pdf.setFontSize(14);
          pdf.text('PRE-EXISTING VEHICLE DAMAGE REPORT', leftMargin, yPosition);
          yPosition += 15;

          // Vehicle Information
          pdf.setFontSize(12);
          if (damageReport.vehicle_make || damageReport.vehicle_model) {
            pdf.text('Vehicle Information:', leftMargin, yPosition);
            yPosition += 8;
            
            pdf.setFont(undefined, 'normal');
            pdf.setFontSize(10);
            
            let vehicleInfo = '';
            if (damageReport.vehicle_year) vehicleInfo += `${damageReport.vehicle_year} `;
            if (damageReport.vehicle_make) vehicleInfo += `${damageReport.vehicle_make} `;
            if (damageReport.vehicle_model) vehicleInfo += `${damageReport.vehicle_model}`;
            
            if (vehicleInfo) {
              pdf.text(`Vehicle: ${vehicleInfo}`, leftMargin, yPosition);
              yPosition += 5;
            }
            
            if (damageReport.vehicle_color) {
              pdf.text(`Color: ${damageReport.vehicle_color}`, leftMargin, yPosition);
              yPosition += 5;
            }
            
            if (damageReport.license_plate) {
              pdf.text(`License Plate: ${damageReport.license_plate}`, leftMargin, yPosition);
              yPosition += 5;
            }
            
            yPosition += 10;
          }

          // Try to add vehicle diagram with damage points
          try {
            await addVehicleDiagramToPDF(pdf, damageReport, leftMargin, yPosition, pageWidth - 40);
            yPosition += 120; // Space for diagram
          } catch (err) {
            console.warn('⚠️ [LIABILITY PDF] Could not add vehicle diagram:', err);
            pdf.setFont(undefined, 'normal');
            pdf.setFontSize(10);
            pdf.text('[Vehicle diagram with damage points - see original report]', leftMargin, yPosition);
            yPosition += 10;
          }

          // Damage Points Summary
          pdf.setFont(undefined, 'bold');
          pdf.setFontSize(12);
          pdf.text(`Documented Damage (${damageReport.damage_points.length} points):`, leftMargin, yPosition);
          yPosition += 10;

          pdf.setFont(undefined, 'normal');
          pdf.setFontSize(10);

          damageReport.damage_points.forEach((point, index) => {
            // Check if we need a new page
            if (yPosition > pdf.internal.pageSize.getHeight() - 30) {
              pdf.addPage();
              yPosition = 30;
            }

            const typeConfig = DAMAGE_TYPES.find(type => type.value === point.damageType);
            const severityConfig = SEVERITY_LEVELS.find(level => level.value === point.severity);
            
            let damageText = `${index + 1}. ${typeConfig?.label || point.damageType} (${severityConfig?.label || point.severity})`;
            if (point.notes) {
              damageText += ` - ${point.notes}`;
            }

            // Split long text to fit page width
            const maxWidth = pageWidth - 40;
            const lines = pdf.splitTextToSize(damageText, maxWidth);
            
            lines.forEach((line: string) => {
              if (yPosition > pdf.internal.pageSize.getHeight() - 20) {
                pdf.addPage();
                yPosition = 30;
              }
              pdf.text(line, leftMargin, yPosition);
              yPosition += 5;
            });
          });

          if (damageReport.additional_notes) {
            yPosition += 5;
            pdf.setFont(undefined, 'bold');
            pdf.text('Additional Notes:', leftMargin, yPosition);
            yPosition += 6;
            
            pdf.setFont(undefined, 'normal');
            const notesLines = pdf.splitTextToSize(damageReport.additional_notes, pageWidth - 40);
            notesLines.forEach((line: string) => {
              if (yPosition > pdf.internal.pageSize.getHeight() - 20) {
                pdf.addPage();
                yPosition = 30;
              }
              pdf.text(line, leftMargin, yPosition);
              yPosition += 5;
            });
          }

          yPosition += 10;
          
          // Damage acknowledgment
          pdf.setFont(undefined, 'bold');
          pdf.setFontSize(10);
          pdf.setTextColor(0, 0, 0);
          
          const acknowledgmentText = 'IMPORTANT: By signing this waiver, you acknowledge that the above damage was present before service began and that our company is not responsible for these pre-existing conditions.';
          const ackLines = pdf.splitTextToSize(acknowledgmentText, pageWidth - 40);
          
          ackLines.forEach((line: string) => {
            if (yPosition > pdf.internal.pageSize.getHeight() - 20) {
              pdf.addPage();
              yPosition = 30;
            }
            pdf.text(line, leftMargin, yPosition);
            yPosition += 5;
          });
          
          yPosition += 15;
        }
        
        // Terms and Conditions Section
        pdf.setFont(undefined, 'bold');
        pdf.setFontSize(12);
        pdf.setTextColor(60, 60, 60);
        pdf.text('Terms and Conditions:', leftMargin, yPosition);
        yPosition += 10;
        
        pdf.setFont(undefined, 'normal');
        pdf.setFontSize(10);
        
        // Split terms content into lines that fit the page width
        const maxWidth = pageWidth - 40;
        const termsLines = pdf.splitTextToSize(waiver.terms_content, maxWidth);
        
        termsLines.forEach((line: string) => {
          // Check if we need a new page
          if (yPosition > pdf.internal.pageSize.getHeight() - 60) {
            pdf.addPage();
            yPosition = 30;
          }
          pdf.text(line, leftMargin, yPosition);
          yPosition += 4;
        });
        
        yPosition += 20;
        
        // Signature Section
        if (yPosition > pdf.internal.pageSize.getHeight() - 80) {
          pdf.addPage();
          yPosition = 30;
        }
        
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        pdf.text('Digital Signature:', leftMargin, yPosition);
        yPosition += 15;
        
        // Add signature image if available
        if (waiver.signature_data) {
          try {
            const signatureWidth = 150;
            const signatureHeight = 50;
            pdf.addImage(waiver.signature_data, 'PNG', leftMargin, yPosition, signatureWidth, signatureHeight);
            yPosition += signatureHeight + 10;
          } catch (err) {
            console.warn('Could not add signature image to PDF:', err);
            pdf.setFont(undefined, 'normal');
            pdf.text('[Digital signature on file]', leftMargin, yPosition);
            yPosition += 10;
          }
        }
        
        // Agreement statement
        pdf.setFont(undefined, 'normal');
        pdf.setFontSize(10);
        pdf.text('I hereby acknowledge that I have read, understood, and agree to be bound by the', leftMargin, yPosition);
        yPosition += 5;
        pdf.text('terms and conditions stated above.', leftMargin, yPosition);
        yPosition += 15;
        
        // Footer with metadata
        pdf.setFontSize(8);
        pdf.setTextColor(100, 100, 100);
        pdf.text(`Document ID: ${waiver.id}`, leftMargin, yPosition);
        yPosition += 4;
        pdf.text(`Token: ${waiver.token}`, leftMargin, yPosition);
        
        if (waiver.ip_address) {
          yPosition += 4;
          pdf.text(`Signed from IP: ${waiver.ip_address}`, leftMargin, yPosition);
        }
        
        console.log('✅ [LIABILITY PDF] PDF generation completed');
        
        // Convert to blob
        const pdfBlob = pdf.output('blob');
        resolve(pdfBlob);
      }).catch((error: any) => {
        console.error('❌ [LIABILITY PDF] Error generating PDF:', error);
        reject(error);
      });
    } catch (error) {
      console.error('❌ [LIABILITY PDF] PDF generation failed:', error);
      reject(error);
    }
  });
}

/**
 * Add vehicle diagram with damage points to PDF
 */
async function addVehicleDiagramToPDF(
  pdf: any, 
  damageReport: any, 
  x: number, 
  y: number, 
  maxWidth: number
): Promise<void> {
  return new Promise((resolve, reject) => {
    try {
      console.log('🚗 [LIABILITY PDF] Adding vehicle diagram...');
      
      // Create a canvas to draw the diagram with damage points
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('Could not get canvas context');
      }
      
      // Set canvas size
      canvas.width = 600;
      canvas.height = 360;
      
      // Load the car diagram image
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          // Draw the car diagram
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          
          // Draw damage points
          damageReport.damage_points.forEach((point: any, index: number) => {
            const typeConfig = DAMAGE_TYPES.find(type => type.value === point.damageType);
            const pointNumber = index + 1;
            
            // Calculate position on canvas
            const pointX = (point.x / 100) * canvas.width;
            const pointY = (point.y / 100) * canvas.height;
            
            // Draw damage point circle
            ctx.fillStyle = typeConfig?.color || '#a0a0a0';
            ctx.strokeStyle = '#333';
            ctx.lineWidth = 2;
            
            ctx.beginPath();
            ctx.arc(pointX, pointY, 12, 0, 2 * Math.PI);
            ctx.fill();
            ctx.stroke();
            
            // Draw point number
            ctx.fillStyle = 'white';
            ctx.font = 'bold 14px Arial';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(pointNumber.toString(), pointX, pointY);
          });
          
          // Convert canvas to image data
          const imageData = canvas.toDataURL('image/png');
          
          // Calculate dimensions for PDF
          const aspectRatio = canvas.height / canvas.width;
          const diagramWidth = Math.min(maxWidth, 150);
          const diagramHeight = diagramWidth * aspectRatio;
          
          // Add image to PDF
          pdf.addImage(imageData, 'PNG', x, y, diagramWidth, diagramHeight);
          
          console.log('✅ [LIABILITY PDF] Vehicle diagram added successfully');
          resolve();
        } catch (err) {
          console.error('❌ [LIABILITY PDF] Error drawing on canvas:', err);
          reject(err);
        }
      };
      
      img.onerror = () => {
        console.error('❌ [LIABILITY PDF] Failed to load car diagram image');
        reject(new Error('Failed to load car diagram image'));
      };
      
      // Load the image
      img.src = '/cardiagram.png.jpg';
      
    } catch (error) {
      console.error('❌ [LIABILITY PDF] Error setting up vehicle diagram:', error);
      reject(error);
    }
  });
}

/**
 * Store liability waiver PDF in Supabase Storage
 */
async function storeLiabilityWaiverPDF(pdfBlob: Blob, filename: string): Promise<string> {
  if (!supabase) {
    throw new Error('Supabase client not initialized');
  }

  console.log('📂 [LIABILITY PDF] Storing PDF in storage:', filename);

  // Upload to Supabase Storage
  const { data, error } = await supabase.storage
    .from('liability-waivers')
    .upload(filename, pdfBlob, {
      contentType: 'application/pdf',
      upsert: true
    });

  if (error) {
    console.error('❌ [LIABILITY PDF] Failed to upload PDF:', error);
    throw new Error(`Failed to upload PDF: ${error.message}`);
  }

  console.log('✅ [LIABILITY PDF] PDF uploaded successfully');

  // Get public URL and replace with custom domain
  const { data: urlData } = supabase.storage
    .from('liability-waivers')
    .getPublicUrl(filename);

  // Replace the default Supabase URL with custom domain
  const publicUrl = urlData.publicUrl.replace(
    'https://idvgoctfaarxiryxprpy.supabase.co',
    'https://api.gitbooked.com'
  );
  
  console.log('✅ [LIABILITY PDF] Generated custom domain URL:', publicUrl);
  
  return publicUrl;
}

/**
 * Generate and store PDF for signed liability waiver
 */
export async function generateAndStoreLiabilityWaiverPDF(waiver: LiabilityWaiver): Promise<string | null> {
  try {
    console.log('🔄 [LIABILITY PDF] Starting PDF generation and storage process...');
    
    // Generate PDF
    const pdfBlob = await generateLiabilityWaiverPDF(waiver);
    console.log('✅ [LIABILITY PDF] PDF generated, size:', pdfBlob.size, 'bytes');
    
    // Generate filename
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const filename = `liability-waiver-${waiver.booking_id}-${timestamp}.pdf`;
    console.log('✅ [LIABILITY PDF] Generated filename:', filename);
    
    // Store PDF
    const publicUrl = await storeLiabilityWaiverPDF(pdfBlob, filename);
    console.log('✅ [LIABILITY PDF] PDF stored successfully');
    
    // Update waiver record with PDF URL
    if (!supabase) {
      throw new Error('Supabase client not initialized for updating waiver');
    }
    
    const { error: updateError } = await supabase
      .from('liability_waivers')
      .update({ pdf_url: publicUrl })
      .eq('id', waiver.id);

    if (updateError) {
      console.error('❌ [LIABILITY PDF] Failed to update waiver with PDF URL:', updateError);
      throw updateError;
    }
    
    console.log('✅ [LIABILITY PDF] Process completed successfully');
    return publicUrl;
    
  } catch (error) {
    console.error('❌ [LIABILITY PDF] PDF generation and storage failed:', error);
    return null;
  }
} 